import AnnouncementBar from '@algbra/ui/components/AnnouncementBar/AnnouncementBar'
import Button from '@algbra/ui/components/Button/Button'
import FreeText from '@algbra/ui/components/FreeText/FreeText'
import { Column, Container, Row } from '@algbra/ui/components/Grid/Grid'
import Heading from '@algbra/ui/components/Heading/Heading'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import { VAlign } from '@algbra/ui/enums/align'
import {
  MEDIA_QUERY_MOBILE,
  useMediaQuery,
} from '@algbra/ui/hooks/useMediaQuery'
import '@algbra/ui/styles/globals.scss'
import classNames from 'classnames'
import { graphql } from 'gatsby'
import React from 'react'
import BusinessWaitlistForm from '../components/BusinessWaitlistForm/BusinessWaitlistForm'
import Footer from '../components/Footer/Footer'
import Header from '../components/Header/Header'
import HeaderContainer from '../components/HeaderContainer/HeaderContainer'
import MetaTags from '../components/MetaTags/MetaTags'
import { getMetaTags } from '../utils/metaTags'
import { getComponentByZoneType, ZoneItemType } from '../zones/HomeContentZone'
import { ReactComponent as CertifiedBCorpLogo } from '../images/home/certified-b-corporation-2.svg'
import { ReactComponent as InfoIcon } from '../images/info-white.svg'
import { ReactComponent as MedalIcon } from '../images/home/medal.svg'
import { ReactComponent as PricelessPlanetCoalitionLogo } from '../images/home/priceless-planet-coalition.svg'
import { ReactComponent as ShieldStarIcon } from '../images/home/shield-star.svg'
import styles from './business.module.scss'
import homeStyles from './home.module.scss'

export default function LayoutBusiness({ pageContext, data, location }) {
  const isMobile = useMediaQuery(MEDIA_QUERY_MOBILE)
  const { contentZones, title, subtitle } = data.strapiGQL.page
  const scrollToWaitlist = () => {
    document
      .getElementById('business-waitlist')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const homeBoxZones: { zone: ZoneItemType; index: number }[] = []
  const restZones: { zone: ZoneItemType; index: number }[] = []
  ;((contentZones || []) as ZoneItemType[]).forEach((zone, index) => {
    if (zone.__typename === 'StrapiGQL_ComponentZonesHomeBoxes') {
      homeBoxZones.push({ zone, index })
    } else {
      restZones.push({ zone, index })
    }
  })

  return (
    <div className={styles.layoutBusiness}>
      <MetaTags {...getMetaTags(pageContext)} />
      <HeaderContainer className={styles.hero}>
        <AnnouncementBar
          title="Algbra Labs bolsters diaspora-focused digital banking leadership with Isbank and RUUT strategic partnership"
          to="/news/algbra-labs-and-moka-united-the-fintech-subsidiary-of-isbank-announce-strategic-partnership-to-launch-ruut-in-the-uk/"
          target="_self"
          category="Press Release"
        />
        <Header
          hasBorder={isMobile}
          hasBg={false}
          hasAnnouncement={true}
          forceTheme="darkTranslucent"
        />
        <Container
          fluid={false}
          className={homeStyles.zIndexed}
          role="complementary"
        >
          <Row vAlign={VAlign.CENTER} className={homeStyles.bannerArea}>
            <Column
              size={12}
              sm={8}
              className={classNames(homeStyles.content, styles.bannerContent)}
            >
              <div>
                <Heading level={1} type="xl" className={styles.header}>
                  <div dangerouslySetInnerHTML={{ __html: title }} />
                </Heading>
                {subtitle && (
                  <FreeText
                    className={classNames(homeStyles.text, homeStyles.subtitle)}
                    innerHTML={subtitle}
                    color="white"
                  >
                    {subtitle}
                  </FreeText>
                )}
              </div>

              <div className={styles.waitlistCta}>
                <Button
                  className={styles.waitlistButton}
                  size="normal"
                  onClick={scrollToWaitlist}
                  theme="light"
                >
                  Join the waitlist
                </Button>
                <div className={styles.availabilityDisclaimer}>
                  <InfoIcon aria-hidden="true" />
                  <p>
                    Feature and service availability varies by country and
                    region. <Permalink to="/help">Learn more.</Permalink>
                  </p>
                </div>
              </div>

              <div className={homeStyles.bottomContainer}>
                <div className={homeStyles.bottomBlock}>
                  <div className={homeStyles.bottomItem}>
                    <MedalIcon />
                    <p className={homeStyles.bottomItemTitle}>
                      Winner of the 2023 Pay360 Award for Best ESG Initiative in
                      Payments
                    </p>
                  </div>
                  <div className={homeStyles.bottomItem}>
                    <ShieldStarIcon />
                    <p className={homeStyles.bottomItemTitle}>
                      Algbra is regulated by the Financial Conduct Authority
                      (FCA) in the UK.
                    </p>
                  </div>
                </div>
                <div className={homeStyles.certificationContainer}>
                  <PricelessPlanetCoalitionLogo />
                  <CertifiedBCorpLogo />
                </div>
              </div>
            </Column>
          </Row>
        </Container>
      </HeaderContainer>
      <main>
        {homeBoxZones.map(({ zone, index }) =>
          getComponentByZoneType(zone, `${zone.__typename}-${index}`, location)
        )}
        <Container id="business-waitlist" className={styles.waitlistSection}>
          <BusinessWaitlistForm />
        </Container>
        {restZones.map(({ zone, index }) =>
          getComponentByZoneType(zone, `${zone.__typename}-${index}`, location)
        )}
      </main>
      <Footer />
    </div>
  )
}

export const pageQuery = graphql`
  query ($id: ID!) {
    strapiGQL {
      page(id: $id) {
        ...StrapiGQL_PagesWithContentZones
      }
    }
  }
`
